import axios from 'axios';

/**
 * Common download request API that preserves response headers
 * Specifically designed for file downloads (PDF, Excel, etc.)
 */
const downloadRequestApi = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
  headers: {
    accept: '*/*',
    language: 'en',
    'Access-Control-Allow-Origin': '*',
  },
});

// Request interceptor to add authorization token
downloadRequestApi.interceptors.request.use(
  (config) => {
    const token =
      JSON.parse(localStorage.getItem('revive_admin__token')) || null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - IMPORTANT: Returns full response object to preserve headers
downloadRequestApi.interceptors.response.use(
  (response) => {
    // For download requests, we need the full response object including headers
    // Do NOT return just response.data like the regular requestApi
    return Promise.resolve(response);
  },
  async (error) => {
    if (error?.response?.status === 422) {
      return await Promise.reject({
        status: 422,
        message: error?.response?.data?.message || 'Validation failed',
      });
    } else if (error?.response?.status === 403) {
      localStorage.clear();
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      return Promise.reject({
        status: 403,
        message: error?.response?.data?.error?.message || 'Forbidden access',
      });
    } else if (error?.response?.status === 400) {
      return Promise.reject({
        status: 400,
        message:
          error?.response?.data?.error?.message || 'Incorrect input value',
      });
    } else if (error?.response?.status === 405) {
      return Promise.reject({
        status: 405,
        message: error?.response?.data?.message || 'Incorrect input value',
      });
    } else if (error?.response?.status === 401) {
      localStorage.clear();
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      return Promise.reject({
        status: 401,
        message:
          error?.response?.data?.message ||
          'Your Session is expired. Please Login again',
      });
    } else {
      let message = 'Something went to wrong.';
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json?.error?.message || json?.message || message;
        } else {
          message = error?.response?.data?.error?.message || message;
        }
      } catch (_) {}
      return Promise.reject({
        message,
        status: error?.response?.status || 500,
      });
    }
  },
);

const resolveFilename = (response, defaultFilename, extension) => {
  // Try multiple ways to get filename
  const xFilename = response.headers['x-filename'];
  const XFilename = response.headers['X-Filename'];
  const contentDisposition = response.headers['content-disposition'];

  // Extract filename from Content-Disposition if available
  let filenameFromDisposition = null;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (filenameMatch && filenameMatch[1]) {
      filenameFromDisposition = filenameMatch[1].replace(/['"]/g, '');
      // Remove the extension if present since we add it back later
      filenameFromDisposition = filenameFromDisposition.replace(
        new RegExp(`\\${extension}$`, 'i'),
        '',
      );
    }
  }

  // Priority: X-Filename header > x-filename header > Content-Disposition > default
  const filename = XFilename || xFilename || filenameFromDisposition;

  if (extension === '.zip') {
    // Remove any existing extension from the filename
    return defaultFilename.split('.')[0];
  }
  return filename || defaultFilename;
};

const triggerBlobDownload = (data, mimeType, filename) => {
  const blob = new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  a.click();

  window.URL.revokeObjectURL(url);
};

/**
 * Download file with proper filename handling
 * @param {string} endpoint - API endpoint
 * @param {object} params - Query parameters
 * @param {string} defaultFilename - Fallback filename if header not present
 * @param {string} mimeType - MIME type for the blob (default: 'application/pdf')
 * @param {string} extension - File extension to append (default: '.pdf')
 * @param {function} onSuccess - Success callback
 * @param {function} onError - Error callback
 */
export const downloadFile = async (
  endpoint,
  params = {},
  defaultFilename = 'download',
  mimeType = 'application/pdf',
  extension = '.pdf',
  onSuccess = null,
  onError = null,
) => {
  try {
    const response = await downloadRequestApi.get(endpoint, {
      params,
      responseType: 'blob',
      headers: {
        'Access-Control-Expose-Headers': 'X-Filename, Content-Disposition',
      },
    });

    const finalFilename = resolveFilename(response, defaultFilename, extension);
    triggerBlobDownload(
      response.data,
      mimeType,
      `${finalFilename}${extension}`,
    );

    if (typeof onSuccess === 'function') {
      onSuccess(finalFilename);
    }

    return { success: true, filename: finalFilename };
  } catch (error) {
    if (typeof onError === 'function') {
      onError(error);
    }

    throw error;
  }
};

/**
 * Download file from a POST request (for endpoints that need a request body,
 * e.g. export field selection) with the same filename handling as downloadFile.
 * @param {string} endpoint - API endpoint
 * @param {object} body - Request body
 * @param {string} defaultFilename - Fallback filename if header not present
 * @param {string} mimeType - MIME type for the blob
 * @param {string} extension - File extension to append (e.g. '.csv')
 * @param {function} onSuccess - Success callback
 * @param {function} onError - Error callback
 */
export const downloadFilePost = async (
  endpoint,
  body = {},
  defaultFilename = 'download',
  mimeType = 'application/octet-stream',
  extension = '',
  onSuccess = null,
  onError = null,
) => {
  try {
    const response = await downloadRequestApi.post(endpoint, body, {
      responseType: 'blob',
      headers: {
        'Access-Control-Expose-Headers': 'X-Filename, Content-Disposition',
      },
    });

    const finalFilename = resolveFilename(response, defaultFilename, extension);
    triggerBlobDownload(
      response.data,
      mimeType,
      `${finalFilename}${extension}`,
    );

    if (typeof onSuccess === 'function') {
      onSuccess(finalFilename);
    }

    return { success: true, filename: finalFilename };
  } catch (error) {
    if (typeof onError === 'function') {
      onError(error);
    }

    throw error;
  }
};

export default downloadRequestApi;
